import React, { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { authAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

/**
 * AdminLogin — dedicated sign-in page for administrators. Uses the same auth
 * endpoint as standard pages, but restricts access specifically to accounts where
 * role === 'admin'.
 */
export default function AdminLogin() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [searchParams] = useSearchParams();
  const nextPath = searchParams.get('next');

  const [form, setForm] = useState({
    email: localStorage.getItem('kanjusee_remember_email') || '',
    password: '',
  });
  const [remember, setRemember] = useState(!!localStorage.getItem('kanjusee_remember_email'));
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const res = await authAPI.login({ ...form, role: 'admin' });
      const user = res.data.data.user;
      if (user.role !== 'admin') {
        setError('Access denied. This login page is only for administrators.');
        return;
      }
      if (remember) localStorage.setItem('kanjusee_remember_email', form.email);
      else localStorage.removeItem('kanjusee_remember_email');
      login(res.data.data.token, user);

      if (nextPath && nextPath.startsWith('/admin')) {
        navigate(nextPath);
      } else {
        navigate('/admin/dashboard');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Login failed.');
    } finally { setLoading(false); }
  };

  return (
    <div className="flex-stack" style={{ minHeight: '100vh', background: 'var(--cream)' }}>
      {/* Left — admin-flavoured branding */}
      <div className="hide-mobile" style={{ flex: 1, background: '#1A1A1A', display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '60px 50px', minWidth: 0 }}>
        <div>
          <span className="logo" style={{ color: '#fff', fontSize: 32 }}>Kanjusee</span>
          <span className="logo-dot" style={{ fontSize: 36 }}>.</span>
          <div style={{ display: 'inline-block', marginLeft: 12, padding: '3px 10px', background: '#DC2626', color: '#FFF', borderRadius: 6, fontSize: 11, fontWeight: 800, letterSpacing: 1, verticalAlign: 'middle' }}>ADMIN</div>
        </div>
        <p style={{ fontFamily: 'var(--font-serif)', fontSize: 28, fontWeight: 700, color: '#fff', marginTop: 24, lineHeight: 1.3 }}>
          Kanjusee Administration.<br />Ecosystem management portal.
        </p>
        <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.6)', marginTop: 12, lineHeight: 1.7 }}>
          Sign in to access platform moderation, wallet adjustments, taxonomies, and analytics.
        </p>

        <div style={{ marginTop: 32, display: 'flex', flexDirection: 'column', gap: 14 }}>
          {[
            { icon: '🛠️', text: 'Platform taxonomy & category tree' },
            { icon: '📊', text: 'Ecosystem-wide analytics and indicators' },
            { icon: '💳', text: 'Multi-vendor wallet balance adjustments' },
            { icon: '📑', text: 'Invoices, GST rules, and templates' },
          ].map((f, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, color: 'rgba(255,255,255,0.75)', fontSize: 14 }}>
              <span style={{ fontSize: 20 }}>{f.icon}</span> {f.text}
            </div>
          ))}
        </div>
      </div>

      {/* Right — form */}
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 24px' }}>
        <div style={{ width: '100%', maxWidth: 380 }}>
          <div className="show-mobile" style={{ textAlign: 'center', marginBottom: 24 }}>
            <Link to="/" style={{ textDecoration: 'none' }}>
              <span className="logo" style={{ color: 'var(--green)', fontSize: 28 }}>Kanjusee</span>
              <span className="logo-dot" style={{ fontSize: 32 }}>.</span>
              <div style={{ display: 'inline-block', marginLeft: 8, padding: '2px 8px', background: '#DC2626', color: '#FFF', borderRadius: 6, fontSize: 10, fontWeight: 800, letterSpacing: 1, verticalAlign: 'middle' }}>ADMIN</div>
            </Link>
          </div>
          <h2 className="heading-md" style={{ marginBottom: 4 }}>Administrator Sign In</h2>
          <p className="text-muted text-sm" style={{ marginBottom: 28 }}>Use your secure credentials to verify access.</p>

          {error && (
            <div style={{ background: 'var(--red-lt)', color: 'var(--red)', padding: '10px 14px', borderRadius: 'var(--radius)', fontSize: 13, marginBottom: 16, fontWeight: 500 }}>{error}</div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label">Email Address</label>
              <input className="form-input" type="email" required autoFocus value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="admin@kanjusee.com" />
            </div>
            <div className="form-group">
              <label className="form-label">Password</label>
              <input className="form-input" type="password" required value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} placeholder="Your password" />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4, marginBottom: 14 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--muted)', cursor: 'pointer' }}>
                <input type="checkbox" checked={remember} onChange={e => setRemember(e.target.checked)}
                  style={{ width: 16, height: 16, accentColor: 'var(--green)', cursor: 'pointer' }} />
                Remember me
              </label>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                <Link to="/forgot-password" style={{ fontSize: 12, color: 'var(--green)', fontWeight: 600, textDecoration: 'none' }}>Forgot password?</Link>
              </div>
            </div>
            <button type="submit" disabled={loading} className="btn btn-pri btn-lg btn-full" style={{ background: '#DC2626', borderColor: '#DC2626' }}>
              {loading ? 'Verifying Admin...' : 'Sign In to Admin Panel →'}
            </button>
          </form>

          <p style={{ textAlign: 'center', marginTop: 32 }}>
            <Link to="/" style={{ fontSize: 12, color: 'var(--muted)', textDecoration: 'none' }}>← Back to home</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
